(function() {
    'use strict';
    
    // Configuration
    const API_BASE_URL = 'https://cws-jura.financepayment.co.uk/api/v3/v12finance/product-page-price';
    const IFRAME_BASE_URL = 'https://cws-jura.financepayment.co.uk/v3/iframe/v12finance/product-page';
    const PRICE_CHECK_INTERVAL = 1000;
    const PRICE_UPDATE_DEBOUNCE = 300;
    const CART_REFRESH_DELAY = 800;
    const IFRAME_LOAD_TIMEOUT = 200;
    
    // DOM Elements Cache
    const elements = {
        ctaContainer: null,
        modalContainer: null,
        modalIframe: null,
        modalCloseBtn: null,
        knowMoreBtn: null,
        addToCartBtn: null,
        loaderElement: null,
        priceElement: null,
        variantSelect: null,
        productForm: null,
        variationContainer: null,
        salePriceElement: null
    };
    
    // State
    let currentPrice = null;
    let financeData = null;
    let currentVariantId = null;
    let lastVariantId = null;
    let priceCheckInterval = null;
    let priceObserver = null;
    let formObserver = null;
    let loadTimeout = null;
    let debounceTimer = null;
    let overlayClickHandler = null;
    let priceFetchController = null;
    let initialized = false;
    
    /**
     * Debounce function to limit API calls
     */
    function debounce(func, wait) {
        return function executedFunction(...args) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => func.apply(this, args), wait);
        };
    }
    
    /**
     * Cache DOM elements for better performance
     */
    function cacheElements() {
        elements.ctaContainer = document.getElementById('autify-product-finance-cta');
        elements.modalContainer = document.getElementById('autify-finance-modal');
        elements.modalIframe = document.getElementById('autify-finance-iframe');
        elements.modalCloseBtn = document.getElementById('autify-finance-modal-close');
        elements.knowMoreBtn = document.getElementById('autify-finance-know-more');
        elements.addToCartBtn = document.querySelector('.autify-addcart-btn');
        elements.loaderElement = document.getElementById('autify-finance-loader');
        elements.variantSelect = document.getElementById('variation');
        elements.productForm = document.getElementById('product-form');
        // Cache sale price element from within the product form
        if (elements.productForm) {
            elements.salePriceElement = elements.productForm.querySelector('.product-item__price--sale');
            elements.priceElement = elements.productForm.querySelector('#product-price') || document.getElementById('product-price');
            elements.variationContainer = elements.productForm.querySelector('.variation');
        }
    }
    
    /**
     * Initialize finance CTA
     */
    function init() {
        // Singleton guard to prevent duplicate initialization
        if (window.__autifyFinanceInitialized) {
            return;
        }
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initFinanceCTA);
        } else {
            initFinanceCTA();
        }
    }
    
    /**
     * Initialize the finance CTA component
     */
    function initFinanceCTA() {
        cacheElements();
        
        if (!elements.ctaContainer) {
            return;
        }
        
        // Ensure CTA is hidden by default on initialization
        hideCTA();
        
        setupEventListeners();
        updateProductInfo();
        setupPriceWatcher();
        
        // Check initial variant availability and show/hide CTA accordingly
        checkAndUpdatePrice();
        
        fetchFinanceDataDebounced();

        // Mark as initialized only after successful setup
        window.__autifyFinanceInitialized = true;
        initialized = true;
    }
    
    /**
     * Setup all event listeners
     */
    function setupEventListeners() {
        if (elements.knowMoreBtn) {
            elements.knowMoreBtn.addEventListener('click', openFinanceModal);
        }
        
        if (elements.modalCloseBtn) {
            elements.modalCloseBtn.addEventListener('click', closeFinanceModal);
        }
        
        if (elements.addToCartBtn) {
            elements.addToCartBtn.addEventListener('click', handleAddToCart);
        }
        
        // Close modal when clicking outside
        if (elements.modalContainer) {
            const overlay = elements.modalContainer.querySelector('.autify-finance-modal__overlay');
            if (overlay) {
                overlay.addEventListener('click', (e) => {
                    if (e.target === overlay) {
                        closeFinanceModal();
                    }
                });
            }
        }
        
        // Close modal on ESC key
        document.addEventListener('keydown', handleEscapeKey);
    }
    
    /**
     * Handle ESC key press
     */
    function handleEscapeKey(e) {
        if (e.key === 'Escape') {
            const quickCartElement = document.getElementById('quick-cart');
            if (quickCartElement?.classList.contains('visible') && quickCartElement.classList.contains('expanded')) {
                closeSidepanel();
            } else {
                closeFinanceModal();
            }
        }
    }
    
    /**
     * Watch for price changes on the product page
     */
    function setupPriceWatcher() {
        // Watch for variant select changes
        if (elements.variantSelect) {
            elements.variantSelect.addEventListener('change', () => {
                // Reset sale price element cache when variant changes
                elements.salePriceElement = null;
                setTimeout(checkAndUpdatePrice, 150);
            });
        }
        
        // Watch for price element changes via MutationObserver
        setupPriceMutationObserver();
        
        // Watch product form for sale price element changes
        if (elements.productForm) {
            formObserver = new MutationObserver(() => {
                // Reset sale price element cache when form content changes
                elements.salePriceElement = null;
                // Re-evaluate price element in case DOM replaced it
                reattachPriceObserverIfNeeded();
            });
            
            formObserver.observe(elements.productForm, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class']
            });
        }
        
        // Periodic check as fallback
        lastVariantId = currentVariantId;
        priceCheckInterval = setInterval(checkAndUpdatePrice, PRICE_CHECK_INTERVAL);
    }
    
    /**
     * Check if product has V12.Disable tag (case-insensitive)
     */
    function isFinanceDisabled() {
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                if (productJson.tags && Array.isArray(productJson.tags)) {
                    // Convert all tags to lowercase for case-insensitive comparison
                    const tagsLowercase = productJson.tags.map(tag => tag.toLowerCase());
                    // Check if product has "v12.disable" tag (any case variation)
                    return tagsLowercase.includes('v12.disable');
                }
            } catch (e) {
                console.error(e);
            }
        }
        return false;
    }
    
    /**
     * Check if current variant is available/in stock
     */
    function isVariantAvailable() {
        if (!currentVariantId) return false;
        
        // Try to get variant info from product JSON
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                if (productJson.variants) {
                    const variant = productJson.variants.find(v => v.id.toString() === currentVariantId.toString());
                    if (variant) {
                        // Variant is available if available property is true
                        return variant.available === true;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }
        
        // Fallback: Check if variant select option is disabled (indicates out of stock)
        if (elements.variantSelect) {
            const selectedOption = elements.variantSelect.options[elements.variantSelect.selectedIndex];
            if (selectedOption && selectedOption.disabled) {
                return false;
            }
        }
        
        // If we can't determine, assume available (let price check determine if finance should show)
        return true;
    }
    
    /**
     * Check and update price - optimized to avoid redundant checks
     */
    function checkAndUpdatePrice() {
        updateProductInfo();
        // Ensure observer follows node replacements
        reattachPriceObserverIfNeeded();
        
        // Check if finance is disabled by product tag - if so, hide and return early
        if (isFinanceDisabled()) {
            hideCTA();
            return;
        }
        
        // Check variant availability and show/hide CTA accordingly
        const variantAvailable = isVariantAvailable();
        if (variantAvailable) {
            const newPrice = getCurrentPrice();
            if (newPrice && (currentVariantId !== lastVariantId || newPrice !== currentPrice)) {
                lastVariantId = currentVariantId;
                if (newPrice !== currentPrice) {
                    currentPrice = newPrice;
                    // Hide CTA immediately when price changes until API confirms it should be shown
                    hideCTA();
                    fetchFinanceDataDebounced();
                }
            }
            // Don't show CTA here - wait for API response to confirm price is valid
            // Only show if we already have finance data and price hasn't changed
            if (!newPrice || newPrice <= 0) {
                hideCTA();
            }
            // If price changed, API call will handle showing/hiding based on response
        } else {
            // Hide CTA if variant is not available
            hideCTA();
            // Reset price tracking when variant becomes unavailable
            if (currentVariantId !== lastVariantId) {
                lastVariantId = currentVariantId;
            }
        }
    }

    /**
     * Ensure price observer is attached to the current price element
     */
    function reattachPriceObserverIfNeeded() {
        const latestPriceEl = (elements.productForm ? elements.productForm.querySelector('#product-price') : null) || document.getElementById('product-price');
        if (latestPriceEl && latestPriceEl !== elements.priceElement) {
            if (priceObserver) {
                try {
                    priceObserver.disconnect();
                } catch (e) {}
            }
            elements.priceElement = latestPriceEl;
            setupPriceMutationObserver();
        }
    }

    /**
     * Setup MutationObserver for the current price element
     */
    function setupPriceMutationObserver() {
        if (!elements.priceElement) return;
        if (priceObserver) {
            try {
                priceObserver.disconnect();
            } catch (e) {}
        }
        priceObserver = new MutationObserver(() => {
            const newPrice = extractPriceFromElement(elements.priceElement);
            if (newPrice && newPrice !== currentPrice) {
                currentPrice = newPrice;
                elements.salePriceElement = null;
                updateProductInfo();
                fetchFinanceDataDebounced();
            }
        });
        priceObserver.observe(elements.priceElement, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['data-price', 'data-product-price']
        });
    }
    
    /**
     * Extract price value from price element
     */
    function extractPriceFromElement(element) {
        if (!element) return null;
        
        // Try data attribute first (fastest)
        if (element.dataset.price) {
            return parseFloat(element.dataset.price);
        }
        
        // Extract from text content
        const text = element.textContent || element.innerText;
        if (!text) return null;
        
        const priceMatch = text.match(/[\d,]+\.?\d*/);
        return priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : null;
    }
    
    /**
     * Get current product price
     */
    function getCurrentPrice() {
        if (!elements.priceElement) {
            // Try to find within product form first, fallback to document
            if (elements.productForm) {
                elements.priceElement = elements.productForm.querySelector('#product-price');
            }
            if (!elements.priceElement) {
                elements.priceElement = document.getElementById('product-price');
            }
        }
        return elements.priceElement ? extractPriceFromElement(elements.priceElement) : null;
    }
    
    /**
     * Debounced version of fetchFinanceData
     */
    const fetchFinanceDataDebounced = debounce(fetchFinanceData, PRICE_UPDATE_DEBOUNCE);
    
    /**
     * Fetch finance data from API
     */
    function fetchFinanceData() {
        currentPrice = getCurrentPrice();
        
        if (!currentPrice || currentPrice <= 0) {
            hideCTA();
            return;
        }
        
        // Hide CTA immediately when starting a new API call
        // It will only be shown if API returns status: "true"
        hideCTA();
        
        // Abort previous in-flight price fetch, if any
        if (priceFetchController) {
            try { priceFetchController.abort(); } catch (e) {}
        }
        priceFetchController = new AbortController();
        
        fetch(`${API_BASE_URL}?price=${currentPrice.toFixed(2)}`, { signal: priceFetchController.signal })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => handleFinanceResponse(data))
            .catch((err) => {
                // Ignore abort errors; only hide CTA on real failures
                if (err && err.name === 'AbortError') return;
                hideCTA();
            });
    }
    
    /**
     * Handle finance API response
     */
    function handleFinanceResponse(response) {
        // Check if finance is disabled by product tag first
        if (isFinanceDisabled()) {
            hideCTA();
            return;
        }
        
        // Only show CTA if:
        // 1. API returns status "true" with valid data
        // 2. Variant is available/in stock
        // 3. Price is within valid range (confirmed by API)
        // 4. Product does not have "V12.Disable" tag
        if (response.status === 'true' && response.data) {
            // Double-check variant availability before showing CTA
            const variantAvailable = isVariantAvailable();
            if (variantAvailable) {
                financeData = response.data;
                updateFinanceDisplay();
                showCTA();
            } else {
                // Variant is not available, hide CTA
                hideCTA();
            }
        } else {
            // Hide CTA if status is "false" or any other error condition
            // This handles cases like price out of range (e.g., "Order value should be between £250 and £27500")
            // or any other API errors
            hideCTA();
        }
    }
    
    /**
     * Update the finance display with data
     */
    function updateFinanceDisplay() {
        if (!financeData) return;
        
        const monthlyEMIEl = document.getElementById('autify-finance-monthly-emi');
        const aprEl = document.getElementById('autify-finance-apr');
        
        if (monthlyEMIEl && financeData.monthly_emi) {
            monthlyEMIEl.textContent = formatCurrency(financeData.monthly_emi);
        }
        
        if (aprEl && financeData.apr) {
            aprEl.textContent = financeData.apr;
        }
    }
    
    /**
     * Format currency value
     */
    function formatCurrency(value) {
        const num = parseFloat(value);
        return isNaN(num) ? '£0.00' : `£${num.toFixed(2)}`;
    }
    
    /**
     * Show the CTA
     */
    function showCTA() {
        if (elements.ctaContainer) {
            elements.ctaContainer.style.display = 'block';
        }
    }
    
    /**
     * Hide the CTA
     */
    function hideCTA() {
        if (elements.ctaContainer) {
            elements.ctaContainer.style.display = 'none';
        }
    }
    
    /**
     * Toggle add to cart button loading state
     */
    function toggleAddToCartButtonState(isLoading) {
        const addToCartBtnElement = document.getElementById('autify-addcart-btn');
        if (addToCartBtnElement) {
            if (isLoading) {
                addToCartBtnElement.classList.add('not-ready');
            } else {
                addToCartBtnElement.classList.remove('not-ready');
            }
        }
    }
    
    /**
     * Get site ID from data attribute
     */
    function getSiteId() {
        if (!elements.ctaContainer) {
            elements.ctaContainer = document.getElementById('autify-product-finance-cta');
        }
        return elements.ctaContainer?.dataset.autifySiteId || null;
    }
    
    /**
     * Check if product is on sale by looking for sale price element
     * Always queries fresh to handle variant changes dynamically
     */
    function isProductOnSale() {
        // Ensure product form is cached
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        if (!elements.productForm) {
            return false;
        }
        
        // Always query fresh to handle variant changes (don't rely on cache)
        const salePriceElement = elements.productForm.querySelector('.product-item__price--sale');
        return salePriceElement !== null;
    }
    
    /**
     * Open finance modal with iframe
     */
    function openFinanceModal() {
        if (!elements.modalContainer || !elements.modalIframe || !currentPrice) return;
        
        // Build iframe URL with price and optional parameters
        let iframeUrl = `${IFRAME_BASE_URL}?price=${currentPrice.toFixed(2)}`;
        const siteId = getSiteId();
        if (siteId) {
            iframeUrl += `&site=${encodeURIComponent(siteId)}`;
        }
        // Add scheme parameter if product is on sale
        if (isProductOnSale()) {
            iframeUrl += `&scheme=secondary`;
        }
        
        // Show loader and disable button
        if (elements.loaderElement) {
            elements.loaderElement.style.display = 'block';
        }
        toggleAddToCartButtonState(true);
        
        // Hide iframe initially
        elements.modalIframe.style.display = 'none';
        
        // Show modal
        elements.modalContainer.style.display = 'block';
        document.body.style.overflow = 'hidden';
        
        // Set iframe source - data-src in HTML prevents initial load, now we trigger it
        elements.modalIframe.src = iframeUrl;
        
        // Setup iframe load handlers
        setupIframeLoadHandlers();
    }
    
    /**
     * Setup iframe load and error handlers
     */
    function setupIframeLoadHandlers() {
        const hideLoaderAndShowIframe = () => {
            if (elements.loaderElement) {
                elements.loaderElement.style.display = 'none';
            }
            toggleAddToCartButtonState(false);
            if (elements.modalIframe) {
                elements.modalIframe.style.display = 'block';
            }
        };
        
        elements.modalIframe.onload = hideLoaderAndShowIframe;
        elements.modalIframe.onerror = hideLoaderAndShowIframe;
        
        // Timeout fallback
        clearTimeout(loadTimeout);
        loadTimeout = setTimeout(() => {
            if (elements.loaderElement?.style.display !== 'none') {
                hideLoaderAndShowIframe();
            }
        }, IFRAME_LOAD_TIMEOUT);
    }
    
    /**
     * Close finance modal
     */
    function closeFinanceModal() {
        if (!elements.modalContainer) return;
            
        clearTimeout(loadTimeout);
        
        elements.modalContainer.style.display = 'none';
            document.body.style.overflow = '';
            
            // Clear iframe source to stop loading
        if (elements.modalIframe) {
            elements.modalIframe.removeAttribute('src');
            elements.modalIframe.style.display = 'none';
            elements.modalIframe.onload = null;
            elements.modalIframe.onerror = null;
        }
        
        // Hide loader and enable button
        if (elements.loaderElement) {
            elements.loaderElement.style.display = 'none';
        }
        toggleAddToCartButtonState(false);
    }
    
    /**
     * Get product and variant ID from page - optimized
     */
    function updateProductInfo() {
        // Re-query if not cached
        if (!elements.variantSelect) {
            elements.variantSelect = document.getElementById('variation');
        }
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        // Method 1: Variant select dropdown
        if (elements.variantSelect?.value) {
            currentVariantId = elements.variantSelect.value;
            return;
        }
        
        // Method 2: Hidden input
        const hiddenInput = document.querySelector('input[name="id"]:not([type="submit"]), input[name="id[]"]');
        if (hiddenInput?.value) {
            currentVariantId = hiddenInput.value;
            return;
        }
        
        // Method 3: Product form data attribute
        if (elements.productForm?.dataset.variantId) {
            currentVariantId = elements.productForm.dataset.variantId;
                return;
            }
        
        // Method 4: Form hidden input
        if (elements.productForm) {
            const formInput = elements.productForm.querySelector('input[name="id"]:not([type="submit"])');
            if (formInput?.value) {
                currentVariantId = formInput.value;
                return;
            }
        }
        
        // Method 5: Product JSON data (last resort)
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl && !currentVariantId) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                const variant = productJson.selected_or_first_available_variant || 
                               (productJson.variants?.length === 1 ? productJson.variants[0] : null);
                if (variant?.id) {
                    currentVariantId = variant.id.toString();
                }
            } catch (e) {
                // Silently fail
            }
        }
    }
    
    /**
     * Get first variant as fallback when no variant is selected but variants exist
     * Only used when product has multiple variants but user hasn't selected one
     */
    function getFirstVariantFallback() {
        // Re-query if not cached
        if (!elements.variantSelect) {
            elements.variantSelect = document.getElementById('variation');
        }
        
        // Method 1: Check variant select dropdown for first option
        if (elements.variantSelect && elements.variantSelect.options.length > 1) {
            // Check if it has more than just placeholder option
            for (let i = 0; i < elements.variantSelect.options.length; i++) {
                const option = elements.variantSelect.options[i];
                // Skip empty/placeholder options
                if (option.value && option.value.trim() !== '' && option.value !== '0') {
                    return option.value;
                }
            }
        }
        
        // Method 2: Check product JSON data
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                
                // Only use fallback if product has multiple variants
                if (productJson.variants && productJson.variants.length > 1) {
                    // Get first available variant
                    const firstVariant = productJson.variants.find(v => v.available) || productJson.variants[0];
                    if (firstVariant?.id) {
                        return firstVariant.id.toString();
                    }
                }
            } catch (e) {
                // Silently fail
            }
        }
        
        // Method 3: Check hidden input - if it exists, it means single variant product
        // Don't use this as fallback since single variant products should already be handled
        // This is just a safety check
        const hiddenInput = document.querySelector('input[name="id"]:not([type="submit"]), input[name="id[]"]');
        if (hiddenInput?.value) {
            // Only use if variant select doesn't exist (single variant product)
            if (!elements.variantSelect) {
                return hiddenInput.value;
            }
        }
        
        return null;
    }
    
    /**
     * Extract properties from product form (hidden inputs and variation selects)
     */
    function extractFormProperties() {
        const properties = {};
        
        // Ensure form is cached
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        if (!elements.productForm) {
            return properties;
        }
        
        // Extract hidden input properties
        const propertyInputs = elements.productForm.querySelectorAll('input[type="hidden"][name^="properties["]');
        propertyInputs.forEach(input => {
            const match = input.name.match(/properties\[(.+?)\]/);
            if (match?.[1] && input.value) {
                properties[match[1]] = input.value;
            }
        });
        
        // Extract select properties from .variation container
        // Re-query if not cached (from within product form)
        if (!elements.variationContainer && elements.productForm) {
            elements.variationContainer = elements.productForm.querySelector('.variation');
        }
        
        if (elements.variationContainer) {
            const propertySelects = elements.variationContainer.querySelectorAll('select[name^="properties["]');
            propertySelects.forEach(select => {
                const match = select.name.match(/properties\[(.+?)\]/);
                // Only include selects with non-empty values
                if (match?.[1] && select.value && select.value.trim() !== '') {
                    properties[match[1]] = select.value;
                }
            });
        }
        
        return properties;
    }
    
    /**
     * Handle Add to Cart click
     */
    function handleAddToCart(e) {
        e.preventDefault();
        updateProductInfo();
        
        // Fallback: If no variant selected but product has variants, use first variant
        if (!currentVariantId) {
            const firstVariant = getFirstVariantFallback();
            if (firstVariant) {
                currentVariantId = firstVariant;
            } else {
                console.error('Autify Finance: No variant ID available');
                alert('Unable to add product to cart. Variant ID not found. Please try again.');
                return;
            }
        }
        
        // Try to trigger existing add to cart button
        const existingBtn = document.querySelector('.product-add-to-cart, [name="add"], .add-to-cart');
        if (existingBtn) {
            existingBtn.click();
            return;
        }
        
        // Fallback: Direct AJAX add to cart
        addToCartDirectly();
    }
    
    /**
     * Refresh cart and open cart drawer
     */
    function refreshCartAndOpenDrawer() {
        // Trigger cart events
        const events = [
            new CustomEvent('productAdded', { bubbles: true, cancelable: true }),
            new CustomEvent('cart:updated'),
            new CustomEvent('ajaxCart:refresh', { bubbles: true, cancelable: true })
        ];
        events.forEach(event => document.dispatchEvent(event));
        
        // jQuery cart refresh if available
        if (typeof jQuery !== 'undefined') {
            jQuery(document).trigger('cart:refresh ajaxCart:refresh');
        }
        
        // Trigger fetchCartItems if available
        if (typeof fetchCartItems === 'function') {
            fetchCartItems();
        }
        
        // Wait for cart to refresh, then open drawer
        setTimeout(() => {
            tryManualSidepanelOpen();
            updateCartCount();
        }, CART_REFRESH_DELAY);
        
        // Show success message if available
        if (typeof window.cartNotification !== 'undefined') {
            window.cartNotification.show();
        }
    }
    
    /**
     * Update cart count in header
     */
    function updateCartCount() {
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                const cartCountElement = document.getElementById('cart-count');
                if (cartCountElement) {
                    cartCountElement.textContent = cart.item_count;
                }
            })
            .catch(() => {
                // Silently fail
            });
    }
    
    /**
     * Directly add product to cart via AJAX
     */
    function addToCartDirectly() {
        updateProductInfo();
        
        // Fallback: If no variant selected but product has variants, use first variant
        if (!currentVariantId) {
            const firstVariant = getFirstVariantFallback();
            if (firstVariant) {
                currentVariantId = firstVariant;
            } else {
            console.error('Autify Finance: No variant ID available');
            alert('Unable to add product to cart. Variant ID not found. Please try again.');
            return;
            }
        }
        
        const addToCartBtn = document.getElementById('autify-addcart-btn');
        const loadingIcon = document.getElementById('autify-product-submit-icon');
        
        // Set loading state
        if (addToCartBtn) {
            addToCartBtn.classList.add('is-loading');
            addToCartBtn.disabled = true;
        }
        if (loadingIcon) {
            loadingIcon.classList.remove('done');
        }
        
        // Prepare request data
        const requestData = {
            id: parseInt(currentVariantId),
            quantity: 1
        };
        
        const properties = extractFormProperties();
            if (Object.keys(properties).length > 0) {
                requestData.properties = properties;
        }
        
        // Make the request
        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
        })
        .then(response => {
            if (!response.ok) {
                return response.json().then(errorData => {
                    throw new Error(errorData.message || errorData.description || `HTTP ${response.status}`);
                }).catch(() => {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                });
            }
            return response.json();
        })
        .then(item => {
            // Show done state
            if (loadingIcon) {
                loadingIcon.classList.add('done');
            }
            
            // Close modal and refresh cart after delay
            setTimeout(() => {
                closeFinanceModal();
                
                // Reset button state
                if (addToCartBtn) {
                    addToCartBtn.classList.remove('is-loading');
                    addToCartBtn.disabled = false;
                }
                if (loadingIcon) {
                    loadingIcon.classList.remove('done');
                }
                
                refreshCartAndOpenDrawer();
            }, 1000);
        })
        .catch(error => {
            console.error('Autify Finance: Error adding to cart', error);
            
            // Reset button state on error
            if (addToCartBtn) {
                addToCartBtn.classList.remove('is-loading');
                addToCartBtn.disabled = false;
            }
            if (loadingIcon) {
                loadingIcon.classList.remove('done');
            }
            
            alert('Unable to add product to cart: ' + error.message);
        });
    }
    
    /**
     * Open the sidepanel using classes
     */
    function tryManualSidepanelOpen() {
            const quickCartElement = document.getElementById('quick-cart');
        const sidepanelOverlay = document.getElementById('sidepanel-overlay');
        
        if (!quickCartElement) return;
        
        quickCartElement.classList.add('visible', 'expanded');
        
        if (sidepanelOverlay) {
            sidepanelOverlay.classList.add('expanded');
            sidepanelOverlay.style.opacity = '1';
            
            // Remove old handler if exists
            if (overlayClickHandler) {
                sidepanelOverlay.removeEventListener('click', overlayClickHandler);
            }
            
            // Add new click handler
            overlayClickHandler = (e) => {
                if (e.target === sidepanelOverlay) {
                closeSidepanel();
                }
            };
            sidepanelOverlay.addEventListener('click', overlayClickHandler);
        }
        
        // Add has-sidepanel class to document elements
        document.documentElement.classList.add('has-sidepanel');
        document.body.classList.add('has-sidepanel');
        const pageElement = document.querySelector('.page');
        if (pageElement) {
            pageElement.classList.add('has-sidepanel');
        }
        
        document.body.style.overflow = 'hidden';
    }
    
    /**
     * Close the sidepanel
     */
    function closeSidepanel() {
        const quickCartElement = document.getElementById('quick-cart');
        const sidepanelOverlay = document.getElementById('sidepanel-overlay');
        
        if (quickCartElement) {
            quickCartElement.classList.remove('visible', 'expanded');
        }
        
        if (sidepanelOverlay) {
            sidepanelOverlay.classList.remove('expanded');
            sidepanelOverlay.style.opacity = '';
            if (overlayClickHandler) {
                sidepanelOverlay.removeEventListener('click', overlayClickHandler);
                overlayClickHandler = null;
            }
        }
        
        // Remove has-sidepanel class
        document.documentElement.classList.remove('has-sidepanel');
        document.body.classList.remove('has-sidepanel');
        const pageElement = document.querySelector('.page');
        if (pageElement) {
            pageElement.classList.remove('has-sidepanel');
        }
        
        document.body.style.overflow = '';
    }
    
    /**
     * Cleanup function to prevent memory leaks
     */
    function cleanup() {
        if (priceCheckInterval) {
            clearInterval(priceCheckInterval);
            priceCheckInterval = null;
        }
        if (priceObserver) {
            priceObserver.disconnect();
            priceObserver = null;
        }
        if (formObserver) {
            formObserver.disconnect();
            formObserver = null;
        }
        if (loadTimeout) {
            clearTimeout(loadTimeout);
            loadTimeout = null;
        }
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        if (priceFetchController) {
            try { priceFetchController.abort(); } catch (e) {}
            priceFetchController = null;
        }
        // Remove document-level listeners
        document.removeEventListener('keydown', handleEscapeKey);
        // Allow re-initialization if needed
        if (initialized) {
            window.__autifyFinanceInitialized = false;
            initialized = false;
        }
    }
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
    
    // Initialize when script loads
    init();
    
})();